import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export const useGSAPAnimations = () => {
  useEffect(() => {
    // Defer GSAP initialization to idle time to reduce main-thread work
    const initializeAnimations = () => {
      // Configure GSAP defaults for smooth animations
      gsap.defaults({
        duration: 0.8,
        ease: "power2.out"
      });

      // Configure ScrollTrigger defaults
      ScrollTrigger.defaults({
        toggleActions: "play none none reverse",
        markers: false,
        scroller: window
      });

      // Performance optimizations
      ScrollTrigger.config({
        autoRefreshEvents: "visibilitychange,DOMContentLoaded,load"
      });

      // Reduce motion for users who prefer it
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        gsap.defaults({ duration: 0.01 });
        return null; // Skip animations for users who prefer reduced motion
      }

      // Create context for cleanup
      const ctx = gsap.context(() => {
        // Initialize only subtle animations
        initializeParallaxAnimations();
        initializeSubtleTextAnimations();
        initializeResponsiveAnimations();
      });

      return ctx;
    };

    // Use requestIdleCallback to defer initialization, fallback to setTimeout
    let ctx = null;
    const idleCallbackId = 'requestIdleCallback' in window
      ? requestIdleCallback(() => {
          ctx = initializeAnimations();
        }, { timeout: 500 })
      : setTimeout(() => {
          ctx = initializeAnimations();
        }, 100);

    return () => {
      // Cancel idle callback if component unmounts before it runs
      if ('requestIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      } else {
        clearTimeout(idleCallbackId);
      }
      // Cleanup all GSAP animations and ScrollTriggers
      if (ctx) {
        ctx.revert();
      }
    };
  }, []);

  const initializeSubtleTextAnimations = () => {
    // Very subtle text animations only for headings
    gsap.utils.toArray('h1, h2').forEach(heading => {
      gsap.fromTo(heading,
        {
          opacity: 0.7,
          y: 10
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power1.out",
          scrollTrigger: {
            trigger: heading,
            start: "top 95%",
            toggleActions: "play none none none"
          }
        }
      );
    });
  };

  const initializeParallaxAnimations = () => {
    // Enhanced parallax effects using GSAP
    gsap.utils.toArray('.parallax-element').forEach(element => {
      const speed = element.dataset.speed || 0.5;

      gsap.to(element, {
        yPercent: -20 * speed, // Reduced movement to prevent gaps
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: 1
        }
      });
    });

    // Background parallax with enhanced performance and gap prevention
    gsap.utils.toArray('.parallax-bg').forEach(bg => {
      const speed = parseFloat(bg.dataset.speed) || 0.3;
      const maxMovement = -15 * speed; // Much smaller movement to prevent gaps

      gsap.to(bg, {
        yPercent: maxMovement,
        ease: "none",
        scrollTrigger: {
          trigger: bg.closest('section'),
          start: "top bottom",
          end: "bottom top",
          scrub: 1
        }
      });
    });
  };


  const initializeResponsiveAnimations = () => {
    // Mobile-specific optimizations
    const mm = gsap.matchMedia();

    // Mobile devices (0-768px)
    mm.add("(max-width: 768px)", () => {
      // Simpler animations for mobile
      gsap.utils.toArray('.parallax-bg').forEach(bg => {
        gsap.set(bg, { y: 0 }); // Disable parallax on mobile for performance
      });

      // No special hero animations needed on mobile
    });

    // Tablet devices (768-1024px)
    mm.add("(min-width: 768px) and (max-width: 1024px)", () => {
      // Medium complexity animations for tablets
      gsap.utils.toArray('.parallax-bg').forEach(bg => {
        const speed = parseFloat(bg.dataset.speed) || 0.3;
        gsap.to(bg, {
          yPercent: -10 * speed, // Even less movement on tablets
          ease: "none",
          scrollTrigger: {
            trigger: bg.closest('section'),
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    });

    // Desktop devices (1024px+)
    mm.add("(min-width: 1024px)", () => {
      // Full parallax effects for desktop only
    });

    return mm;
  };
};

// Utility function for creating smooth scroll-to animations
export const smoothScrollTo = (target, duration = 0.8) => {
  const element = typeof target === 'string' ? document.querySelector(target) : target;
  if (!element) return;

  // Calculate offset based on screen size
  const isMobile = window.innerWidth < 768;
  const offset = isMobile ? 20 : 100; // Much smaller offset on mobile

  gsap.to(window, {
    duration,
    scrollTo: {
      y: element,
      offsetY: offset
    },
    ease: "power2.inOut", // Smooth start and end
    immediateRender: true
  });
};
